import { Card } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useBookingKpis } from "../../app/hooks/useMoney.js";
import styles from "./BookingKpiStrip.module.scss";

// The spec's four tiles, in order and with its `· YTD` qualifier — the figure is year-to-date and the
// sub-line is lifetime, so the label has to say which.
//
// THREE are money and the fourth is a HEADCOUNT. The reference's tile reads `Tickets · YTD / 0 / Lifetime 0`
// — a bare integer where its neighbours read `$0` — and the legacy strip accumulated `presale + walkUp`. This
// column was `ticketsCents` until 2026-08-23: the server sent `sold × price` and this file ran it through the
// currency formatter, so the tile showed gross door REVENUE under a headcount's label. `count: true` is what
// keeps the two apart now.
const CARDS = [
  { key: "guaranteesCents", label: "Guarantees · YTD" },
  { key: "merchCents", label: "Merch · YTD" },
  { key: "ticketsSold", label: "Tickets · YTD", count: true },
  { key: "pnlCents", label: "P&L · YTD" },
] as const;

function usd(cents: number | undefined): string {
  return ((cents ?? 0) / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** A headcount, grouped but never given a currency symbol or a decimal. */
function count(sold: number | undefined): string {
  return (sold ?? 0).toLocaleString();
}

/**
 * The Booking KPI money strip — 4 financial cards, each showing the YTD figure and a lifetime sub-line.
 *
 * This is the FRONTEND half of the A3/B2 money gate: money is a SEPARATE, capability-gated component, never
 * conditional fields on a shared view. It self-gates on `money:lifetime` and returns null otherwise, so a
 * per_show Tour Manager, a Band Member, or a Label NEVER mounts it — and thus never calls `/booking/kpis`
 * (which the server 403s for them regardless). Lifetime totals are SERVER-computed; the client only
 * renders them.
 */
export function BookingKpiStrip() {
  const canLifetime = useCapability("money:lifetime");
  const kpis = useBookingKpis(canLifetime);

  // No lifetime-money seat ⇒ no money strip at all (the money-free viewer sees a money-free Booking tab).
  if (!canLifetime) return null;

  return (
    <section className={styles.strip} aria-label="Financial KPIs (year to date and lifetime)">
      {CARDS.map((card) => {
        const ytd = kpis.data?.[card.key];
        const lifetime = kpis.data?.lifetime?.[card.key];
        const format = "count" in card && card.count ? count : usd;
        return (
          // The tile shell is the `Card` primitive; its three lines are ONE child so the card's own gap
          // never opens up between the label, the figure and the lifetime line.
          <Card key={card.key}>
            <div className={styles.tile}>
              <span className={styles.cardLabel}>{card.label}</span>
              <span className={styles.cardValue}>
                {kpis.isLoading ? "—" : kpis.isError ? "n/a" : format(ytd)}
              </span>
              <span className={styles.cardSub}>
                {kpis.isLoading || kpis.isError ? " " : `Lifetime ${format(lifetime)}`}
              </span>
            </div>
          </Card>
        );
      })}
    </section>
  );
}
